<template>
  <div class="canvas-container">
    <!-- 新增背景图片容器，作为Canvas的底层背景 -->
    <div class="canvas-bg" :style="{ backgroundImage: `url(${url})` }" v-if="url"></div>
    <canvas ref="画布引用" class="canvasRef" @mousedown="处理鼠标按下($event)" @mousemove="处理鼠标移动($event)" @mouseup="处理鼠标抬起($event)" @mouseleave="处理鼠标抬起($event)" @contextmenu="处理右键删除($event)"></canvas>
  </div>
</template>

<script>
export default {
  name: "CanvasDrawRect",
  data() {
    return {
      是否正在绘制: false, // 是否正在绘制
      是否正在调整大小: false, // 是否正在调整矩形大小
      是否正在移动: false, // 是否正在移动矩形
      起始横坐标: 0, // 拖拽起始X坐标
      起始纵坐标: 0, // 拖拽起始Y坐标
      结束横坐标: 0, // 拖拽结束X坐标
      结束纵坐标: 0, // 拖拽结束Y坐标
      画布上下文: null, // Canvas 上下文对象
      矩形数组: [], // 存储所有已绘制的矩形 [{ 唯一标识, 横坐标, 纵坐标, 宽度, 高度 }]
      下一个矩形标识: 1, // 矩形唯一ID生成器
      悬停的矩形标识: null, // 鼠标悬停的矩形ID
      鼠标悬停位置类型: null, // 鼠标悬停的位置类型：corner-tl/corner-tr/corner-bl/corner-br/edge-top/edge-right/edge-bottom/edge-left/inner
      调整中的矩形标识: null, // 正在调整大小的矩形ID
      移动中的矩形标识: null, // 正在移动的矩形ID
      调整类型: null, // 调整类型（和鼠标悬停位置类型一致）
      检测阈值: 8, // 检测边/角的像素阈值（越大越容易触发）
      最小尺寸: 10, // 矩形最小宽高（避免调整到0）
      初始矩形状态: null, // 调整/移动前的矩形初始状态
      控制点半径: 6, // 控制点圆圈的半径
      控制点颜色: "#ff0000", // 控制点圆圈的颜色
    };
  },
  props: {
    url: {
      type: String,
      default: ''
    },
    // ========== 新增：接收外部回显的矩形数据 ==========
    rectData: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    // 监听url变化，重新初始化画布（确保背景图更新）
    url(newVal) {
      if (newVal) {
        this.初始化画布();
      }
    },
    // ========== 新增：监听外部矩形数据变化，触发回显 ==========
    rectData: {
      deep: true, // 深度监听数组/对象变化
      immediate: true, // 组件初始化时立即执行
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.初始化回显矩形(newVal);
        }
      }
    }
  },
  mounted() {
    this.初始化画布();
    window.addEventListener('resize', this.初始化画布);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.初始化画布);
  },
  methods: {
    // ========== 新增：初始化回显矩形数据 ==========
    初始化回显矩形(外部矩形数据) {

      // 先判断画布上下文是否存在，不存在则等待初始化画布后再执行
      if (!this.画布上下文) {
        // 延迟执行（等Canvas挂载完成）
        this.$nextTick(() => {
          // 确保画布已初始化
          this.初始化画布();
          this.初始化回显矩形(外部矩形数据);
        });
        return;
      }


      // 清空原有矩形数据
      this.矩形数组 = [];
      // 记录最大的唯一标识，保证新增矩形ID不重复
      let 最大标识 = 0;

      // 映射外部数据为组件内部格式
      外部矩形数据.forEach(矩形项 => {
        const 内部矩形 = {
          唯一标识: 矩形项.唯一标识 || 最大标识 + 1, // 优先用外部的唯一标识
          横坐标: Number(矩形项.横坐标) || 0, // 转为数字，避免字符串问题
          纵坐标: Number(矩形项.纵坐标) || 0,
          宽度: Number(矩形项.宽度) || 0,
          高度: Number(矩形项.高度) || 0,
          点击跳转: 矩形项.点击跳转 || '' // 保留点击跳转属性（原组件未用到，后续可扩展）
        };
        this.矩形数组.push(内部矩形);

        // 更新最大标识
        if (内部矩形.唯一标识 > 最大标识) {
          最大标识 = 内部矩形.唯一标识;
        }
      });

      // 更新下一个矩形标识，避免ID重复
      this.下一个矩形标识 = 最大标识 + 1;
      // 重绘画布，显示回显的矩形
      this.重绘所有矩形();
    },

    // ========== 原有方法：统一派发矩形数据变化事件 ==========
    派发矩形数据变化事件(操作类型 = 'unknown') {
      // 深拷贝避免父组件修改原数据
      const 矩形数据副本 = JSON.parse(JSON.stringify(this.矩形数组));
      // 派发事件给父组件，传递最新数据和操作类型
      this.$emit('rect-data-change', {
        data: 矩形数据副本,
        operation: 操作类型, // 操作类型：draw/resize/move/delete/clear
      });
      // 控制台打印（可选，方便调试）
      console.log(`[矩形数据变化] ${操作类型}`, 矩形数据副本);
    },

    // 初始化 Canvas 上下文 + 适配尺寸
    初始化画布() {
      const 画布 = this.$refs.画布引用;
      if (!画布) return;

      const 画布位置信息 = 画布.getBoundingClientRect();
      画布.width = 画布位置信息.width;
      画布.height = 画布位置信息.height;

      this.画布上下文 = 画布.getContext("2d");
      this.画布上下文.strokeStyle = "#e71c1c";
      this.画布上下文.lineWidth = 2;
      
      // 尺寸变化后重绘所有矩形
      this.重绘所有矩形();
    },

    // 鼠标按下：开始绘制/调整大小/移动，记录起始坐标
    处理鼠标按下(事件) {
      // 右键不触发绘制/调整/移动
      if (事件.button === 2) return;
      
      const 画布位置信息 = this.$refs.画布引用.getBoundingClientRect();
      const 鼠标横坐标 = 事件.clientX - 画布位置信息.left;
      const 鼠标纵坐标 = 事件.clientY - 画布位置信息.top;

      // 优先处理调整大小
      if (this.悬停的矩形标识 && this.鼠标悬停位置类型 && !['inner', null].includes(this.鼠标悬停位置类型)) {
        this.是否正在调整大小 = true;
        this.调整中的矩形标识 = this.悬停的矩形标识;
        this.调整类型 = this.鼠标悬停位置类型;
        // 记录调整起始坐标和矩形初始状态
        this.起始横坐标 = 鼠标横坐标;
        this.起始纵坐标 = 鼠标纵坐标;
        
        // 找到调整中的矩形并记录初始状态
        const 调整的矩形 = this.矩形数组.find(矩形 => 矩形.唯一标识 === this.调整中的矩形标识);
        if (调整的矩形) {
          this.初始矩形状态 = {
            横坐标: 调整的矩形.横坐标,
            纵坐标: 调整的矩形.纵坐标,
            宽度: 调整的矩形.宽度,
            高度: 调整的矩形.高度
          };
        }
        return;
      }

      // 其次处理移动
      if (this.悬停的矩形标识 && this.鼠标悬停位置类型 === 'inner') {
        this.是否正在移动 = true;
        this.移动中的矩形标识 = this.悬停的矩形标识;
        // 记录移动起始坐标和矩形初始状态
        this.起始横坐标 = 鼠标横坐标;
        this.起始纵坐标 = 鼠标纵坐标;
        
        // 找到移动中的矩形并记录初始状态
        const 移动的矩形 = this.矩形数组.find(矩形 => 矩形.唯一标识 === this.移动中的矩形标识);
        if (移动的矩形) {
          this.初始矩形状态 = {
            横坐标: 移动的矩形.横坐标,
            纵坐标: 移动的矩形.纵坐标,
            宽度: 移动的矩形.宽度,
            高度: 移动的矩形.高度
          };
        }
        return;
      }

      // 最后处理绘制新矩形
      this.是否正在绘制 = true;
      this.起始横坐标 = 鼠标横坐标;
      this.起始纵坐标 = 鼠标纵坐标;
      this.结束横坐标 = this.起始横坐标;
      this.结束纵坐标 = this.起始纵坐标;

      // 清空悬停状态
      this.悬停的矩形标识 = null;
      this.鼠标悬停位置类型 = null;
    },

    // 鼠标移动：实时绘制临时矩形 / 调整矩形大小 / 移动矩形 + 检测悬停矩形
    处理鼠标移动(事件) {
      const 画布位置信息 = this.$refs.画布引用.getBoundingClientRect();
      const 鼠标横坐标 = 事件.clientX - 画布位置信息.left;
      const 鼠标纵坐标 = 事件.clientY - 画布位置信息.top;

      // 1. 正在调整大小
      if (this.是否正在调整大小 && this.调整中的矩形标识) {
        this.调整矩形大小(鼠标横坐标, 鼠标纵坐标);
        this.重绘所有矩形();
        // 调整过程中实时派发事件
        this.派发矩形数据变化事件('resize');
        return;
      }

      // 2. 正在移动矩形
      if (this.是否正在移动 && this.移动中的矩形标识) {
        this.移动矩形(鼠标横坐标, 鼠标纵坐标);
        this.重绘所有矩形();
        // 移动过程中实时派发事件
        this.派发矩形数据变化事件('move');
        return;
      }

      // 3. 检测鼠标悬停的矩形和具体位置（非调整/移动状态时）
      this.检测悬停的矩形和位置(鼠标横坐标, 鼠标纵坐标);

      // 4. 正在绘制时，更新临时矩形坐标并绘制
      if (this.是否正在绘制) {
        this.结束横坐标 = 鼠标横坐标;
        this.结束纵坐标 = 鼠标纵坐标;
        this.重绘所有矩形(); // 重绘所有已保存矩形
        this.绘制临时矩形(); // 绘制当前临时矩形
        this.绘制临时矩形控制点(); // 绘制临时矩形的控制点圆圈
      }
    },

    // 移动矩形的核心逻辑
    移动矩形(当前鼠标X, 当前鼠标Y) {
      // 计算鼠标移动的偏移量
      const 偏移X = 当前鼠标X - this.起始横坐标;
      const 偏移Y = 当前鼠标Y - this.起始纵坐标;
      
      // 找到要移动的矩形
      const 矩形 = this.矩形数组.find(矩形 => 矩形.唯一标识 === this.移动中的矩形标识);
      if (!矩形 || !this.初始矩形状态) return;

      // 更新矩形位置
      矩形.横坐标 = this.初始矩形状态.横坐标 + 偏移X;
      矩形.纵坐标 = this.初始矩形状态.纵坐标 + 偏移Y;

      // 修正矩形位置，保证不超出画布范围
      this.修正矩形位置(矩形);
    },

    // 调整矩形大小的核心逻辑
    调整矩形大小(当前鼠标X, 当前鼠标Y) {
      // 计算鼠标移动的偏移量
      const 偏移X = 当前鼠标X - this.起始横坐标;
      const 偏移Y = 当前鼠标Y - this.起始纵坐标;
      
      // 找到要调整的矩形
      const 矩形 = this.矩形数组.find(矩形 => 矩形.唯一标识 === this.调整中的矩形标识);
      if (!矩形 || !this.初始矩形状态) return;

      // 根据不同的调整类型更新矩形尺寸
      switch (this.调整类型) {
        // 四个角调整
        case 'corner-tl': // 左上角
          矩形.横坐标 = this.初始矩形状态.横坐标 + 偏移X;
          矩形.纵坐标 = this.初始矩形状态.纵坐标 + 偏移Y;
          矩形.宽度 = this.初始矩形状态.宽度 - 偏移X;
          矩形.高度 = this.初始矩形状态.高度 - 偏移Y;
          break;
          
        case 'corner-tr': // 右上角
          矩形.纵坐标 = this.初始矩形状态.纵坐标 + 偏移Y;
          矩形.宽度 = this.初始矩形状态.宽度 + 偏移X;
          矩形.高度 = this.初始矩形状态.高度 - 偏移Y;
          break;
          
        case 'corner-bl': // 左下角
          矩形.横坐标 = this.初始矩形状态.横坐标 + 偏移X;
          矩形.宽度 = this.初始矩形状态.宽度 - 偏移X;
          矩形.高度 = this.初始矩形状态.高度 + 偏移Y;
          break;
          
        case 'corner-br': // 右下角
          矩形.宽度 = this.初始矩形状态.宽度 + 偏移X;
          矩形.高度 = this.初始矩形状态.高度 + 偏移Y;
          break;
          
        // 四条边调整
        case 'edge-top': // 上边
          矩形.纵坐标 = this.初始矩形状态.纵坐标 + 偏移Y;
          矩形.高度 = this.初始矩形状态.高度 - 偏移Y;
          break;
          
        case 'edge-right': // 右边
          矩形.宽度 = this.初始矩形状态.宽度 + 偏移X;
          break;
          
        case 'edge-bottom': // 下边
          矩形.高度 = this.初始矩形状态.高度 + 偏移Y;
          break;
          
        case 'edge-left': // 左边
          矩形.横坐标 = this.初始矩形状态.横坐标 + 偏移X;
          矩形.宽度 = this.初始矩形状态.宽度 - 偏移X;
          break;
      }

      // 修正矩形尺寸，保证宽高不小于最小值，坐标为正
      this.修正矩形尺寸(矩形);
    },

    // 修正矩形位置，防止超出画布范围
    修正矩形位置(矩形) {
      const 画布 = this.$refs.画布引用;
      
      // 横坐标修正（不超出左边界）
      if (矩形.横坐标 < 0) {
        矩形.横坐标 = 0;
      }
      
      // 纵坐标修正（不超出上边界）
      if (矩形.纵坐标 < 0) {
        矩形.纵坐标 = 0;
      }
      
      // 横坐标修正（不超出右边界）
      const 最大横坐标 = 画布.width - 矩形.宽度;
      if (矩形.横坐标 > 最大横坐标) {
        矩形.横坐标 = 最大横坐标;
      }
      
      // 纵坐标修正（不超出下边界）
      const 最大纵坐标 = 画布.height - 矩形.高度;
      if (矩形.纵坐标 > 最大纵坐标) {
        矩形.纵坐标 = 最大纵坐标;
      }
    },

    // 修正矩形尺寸，防止出现负数或过小的尺寸
    修正矩形尺寸(矩形) {
      // 宽度修正
      if (矩形.宽度 < this.最小尺寸) {
        // 根据调整类型决定修正方式
        if (['corner-tl', 'corner-bl', 'edge-left'].includes(this.调整类型)) {
          矩形.横坐标 = 矩形.横坐标 + (this.最小尺寸 - 矩形.宽度);
        }
        矩形.宽度 = this.最小尺寸;
      }

      // 高度修正
      if (矩形.高度 < this.最小尺寸) {
        if (['corner-tl', 'corner-tr', 'edge-top'].includes(this.调整类型)) {
          矩形.纵坐标 = 矩形.纵坐标 + (this.最小尺寸 - 矩形.高度);
        }
        矩形.高度 = this.最小尺寸;
      }

      // 修正位置（调整大小后可能超出画布）
      this.修正矩形位置(矩形);
    },

    // 鼠标松开：结束绘制/调整大小/移动
    处理鼠标抬起() {
      // 结束调整大小
      if (this.是否正在调整大小) {
        this.是否正在调整大小 = false;
        this.调整中的矩形标识 = null;
        this.调整类型 = null;
        this.初始矩形状态 = null;
        console.log('调整后的矩形数据', this.矩形数组);
        // 调整结束后派发事件
        this.派发矩形数据变化事件('resize-end');
        return;
      }

      // 结束移动
      if (this.是否正在移动) {
        this.是否正在移动 = false;
        this.移动中的矩形标识 = null;
        this.初始矩形状态 = null;
        console.log('移动后的矩形数据', this.矩形数组);
        // 移动结束后派发事件
        this.派发矩形数据变化事件('move-end');
        return;
      }

      // 结束绘制
      if (!this.是否正在绘制) return;
      this.是否正在绘制 = false;

      // 计算矩形最终坐标（避免绘制0尺寸矩形）
      const 横坐标 = Math.min(this.起始横坐标, this.结束横坐标);
      const 纵坐标 = Math.min(this.起始纵坐标, this.结束纵坐标);
      const 宽度 = Math.abs(this.结束横坐标 - this.起始横坐标);
      const 高度 = Math.abs(this.结束纵坐标 - this.起始纵坐标);

      // 过滤掉宽高过小的无效矩形
      if (宽度 > this.最小尺寸 && 高度 > this.最小尺寸) {
        this.矩形数组.push({
          唯一标识: this.下一个矩形标识++,
          横坐标,
          纵坐标,
          宽度,
          高度,
        });
        // 绘制完成后派发事件
        this.派发矩形数据变化事件('draw');
      }

      // 重绘所有矩形（不再显示临时矩形）
      this.重绘所有矩形();
      console.log('所有矩形数据', this.矩形数组);
    },

    // 右键事件：删除选中的矩形
    处理右键删除(事件) {
      事件.preventDefault(); // 阻止默认右键菜单
      if (this.悬停的矩形标识) {
        // 过滤掉被选中删除的矩形
        this.矩形数组 = this.矩形数组.filter(矩形 => 矩形.唯一标识 !== this.悬停的矩形标识);
        this.悬停的矩形标识 = null;
        this.鼠标悬停位置类型 = null;
        this.重绘所有矩形(); // 重绘剩余矩形
        // 删除矩形后派发事件
        this.派发矩形数据变化事件('delete');
      }
    },

    // 检测鼠标悬停的矩形和具体位置（内部/边/角）
    检测悬停的矩形和位置(鼠标横坐标, 鼠标纵坐标) {
      if (this.是否正在调整大小 || this.是否正在移动) return; // 调整/移动时不检测悬停

      let 悬停标识 = null;
      let 位置类型 = null;
      const 阈值 = this.检测阈值;

      // 倒序检测（后绘制的矩形在上方，优先检测）
      for (let i = this.矩形数组.length - 1; i >= 0; i--) {
        const 矩形 = this.矩形数组[i];
        const 矩形右边界 = 矩形.横坐标 + 矩形.宽度;
        const 矩形下边界 = 矩形.纵坐标 + 矩形.高度;

        // 先判断是否在矩形范围内
        if (
          鼠标横坐标 >= 矩形.横坐标 - 阈值 &&
          鼠标横坐标 <= 矩形右边界 + 阈值 &&
          鼠标纵坐标 >= 矩形.纵坐标 - 阈值 &&
          鼠标纵坐标 <= 矩形下边界 + 阈值
        ) {
          悬停标识 = 矩形.唯一标识;

          // 1. 检测四个角
          // 左上角
          if (
            Math.abs(鼠标横坐标 - 矩形.横坐标) <= 阈值 &&
            Math.abs(鼠标纵坐标 - 矩形.纵坐标) <= 阈值
          ) {
            位置类型 = 'corner-tl';
            break;
          }
          // 右上角
          else if (
            Math.abs(鼠标横坐标 - 矩形右边界) <= 阈值 &&
            Math.abs(鼠标纵坐标 - 矩形.纵坐标) <= 阈值
          ) {
            位置类型 = 'corner-tr';
            break;
          }
          // 左下角
          else if (
            Math.abs(鼠标横坐标 - 矩形.横坐标) <= 阈值 &&
            Math.abs(鼠标纵坐标 - 矩形下边界) <= 阈值
          ) {
            位置类型 = 'corner-bl';
            break;
          }
          // 右下角
          else if (
            Math.abs(鼠标横坐标 - 矩形右边界) <= 阈值 &&
            Math.abs(鼠标纵坐标 - 矩形下边界) <= 阈值
          ) {
            位置类型 = 'corner-br';
            break;
          }
          // 2. 检测四条边
          // 上边
          else if (
            Math.abs(鼠标纵坐标 - 矩形.纵坐标) <= 阈值 &&
            鼠标横坐标 > 矩形.横坐标 + 阈值 &&
            鼠标横坐标 < 矩形右边界 - 阈值
          ) {
            位置类型 = 'edge-top';
            break;
          }
          // 右边
          else if (
            Math.abs(鼠标横坐标 - 矩形右边界) <= 阈值 &&
            鼠标纵坐标 > 矩形.纵坐标 + 阈值 &&
            鼠标纵坐标 < 矩形下边界 - 阈值
          ) {
            位置类型 = 'edge-right';
            break;
          }
          // 下边
          else if (
            Math.abs(鼠标纵坐标 - 矩形下边界) <= 阈值 &&
            鼠标横坐标 > 矩形.横坐标 + 阈值 &&
            鼠标横坐标 < 矩形右边界 - 阈值
          ) {
            位置类型 = 'edge-bottom';
            break;
          }
          // 左边
          else if (
            Math.abs(鼠标横坐标 - 矩形.横坐标) <= 阈值 &&
            鼠标纵坐标 > 矩形.纵坐标 + 阈值 &&
            鼠标纵坐标 < 矩形下边界 - 阈值
          ) {
            位置类型 = 'edge-left';
            break;
          }
          // 3. 矩形内部
          else if (
            鼠标横坐标 >= 矩形.横坐标 &&
            鼠标横坐标 <= 矩形右边界 &&
            鼠标纵坐标 >= 矩形.纵坐标 &&
            鼠标纵坐标 <= 矩形下边界
          ) {
            位置类型 = 'inner';
            break;
          }
        }
      }

      this.悬停的矩形标识 = 悬停标识;
      this.鼠标悬停位置类型 = 位置类型;
      // 根据悬停位置设置不同的鼠标样式
      this.设置鼠标样式();
    },

    // 根据悬停位置设置鼠标样式
    设置鼠标样式() {
      const 画布 = this.$refs.画布引用;
      if (!画布) return;

      const 样式映射 = {
        // 四个角：对角线调整样式
        'corner-tl': 'nwse-resize',
        'corner-tr': 'nesw-resize',
        'corner-bl': 'nesw-resize',
        'corner-br': 'nwse-resize',
        // 四条边：水平/垂直调整样式
        'edge-top': 'ns-resize',
        'edge-right': 'ew-resize',
        'edge-bottom': 'ns-resize',
        'edge-left': 'ew-resize',
        // 内部：移动样式
        'inner': 'move',
        // 默认：十字绘制样式
        'default': 'crosshair'
      };

      // 获取对应的鼠标样式，无匹配则用默认
      const 鼠标样式 = this.鼠标悬停位置类型 ? 样式映射[this.鼠标悬停位置类型] : 样式映射['default'];
      画布.style.cursor = 鼠标样式;
    },

    // 绘制临时矩形（正在拖拽的矩形）
    绘制临时矩形() {
      const 横坐标 = Math.min(this.起始横坐标, this.结束横坐标);
      const 纵坐标 = Math.min(this.起始纵坐标, this.结束纵坐标);
      const 宽度 = Math.abs(this.结束横坐标 - this.起始横坐标);
      const 高度 = Math.abs(this.结束纵坐标 - this.起始纵坐标);

      this.画布上下文.beginPath();
      this.画布上下文.rect(横坐标, 纵坐标, 宽度, 高度);
      this.画布上下文.strokeStyle = "#ff0000"; // 临时矩形黑色
      this.画布上下文.stroke();
    },

    // 绘制临时矩形的控制点圆圈
    绘制临时矩形控制点() {
      const 横坐标 = Math.min(this.起始横坐标, this.结束横坐标);
      const 纵坐标 = Math.min(this.起始纵坐标, this.结束纵坐标);
      const 宽度 = Math.abs(this.结束横坐标 - this.起始横坐标);
      const 高度 = Math.abs(this.结束纵坐标 - this.起始纵坐标);
      
      // 绘制8个控制点：4个角 + 4条边中点
      this.绘制控制点圆圈([
        // 四个角
        { x: 横坐标, y: 纵坐标 }, // 左上角
        { x: 横坐标 + 宽度, y: 纵坐标 }, // 右上角
        { x: 横坐标, y: 纵坐标 + 高度 }, // 左下角
        { x: 横坐标 + 宽度, y: 纵坐标 + 高度 }, // 右下角
        // 四条边中点
        { x: 横坐标 + 宽度 / 2, y: 纵坐标 }, // 上边中点
        { x: 横坐标 + 宽度, y: 纵坐标 + 高度 / 2 }, // 右边中点
        { x: 横坐标 + 宽度 / 2, y: 纵坐标 + 高度 }, // 下边中点
        { x: 横坐标, y: 纵坐标 + 高度 / 2 } // 左边中点
      ]);
    },

    // 绘制单个矩形的控制点圆圈
    绘制矩形控制点(矩形) {
      // 绘制8个控制点：4个角 + 4条边中点
      this.绘制控制点圆圈([
        // 四个角
        { x: 矩形.横坐标, y: 矩形.纵坐标 }, // 左上角
        { x: 矩形.横坐标 + 矩形.宽度, y: 矩形.纵坐标 }, // 右上角
        { x: 矩形.横坐标, y: 矩形.纵坐标 + 矩形.高度 }, // 左下角
        { x: 矩形.横坐标 + 矩形.宽度, y: 矩形.纵坐标 + 矩形.高度 }, // 右下角
        // 四条边中点
        { x: 矩形.横坐标 + 矩形.宽度 / 2, y: 矩形.纵坐标 }, // 上边中点
        { x: 矩形.横坐标 + 矩形.宽度, y: 矩形.纵坐标 + 矩形.高度 / 2 }, // 右边中点
        { x: 矩形.横坐标 + 矩形.宽度 / 2, y: 矩形.纵坐标 + 矩形.高度 }, // 下边中点
        { x: 矩形.横坐标, y: 矩形.纵坐标 + 矩形.高度 / 2 } // 左边中点
      ]);
    },

    // 绘制控制点圆圈（通用方法）
    绘制控制点圆圈(控制点列表) {
      const 半径 = this.控制点半径;
      this.画布上下文.fillStyle = this.控制点颜色;
      this.画布上下文.strokeStyle = "#ffffff"; // 圆圈白色边框
      this.画布上下文.lineWidth = 1;

      控制点列表.forEach(点 => {
        this.画布上下文.beginPath();
        // 绘制圆形
        this.画布上下文.arc(点.x, 点.y, 半径, 0, Math.PI * 2);
        // 填充圆圈
        this.画布上下文.fill();
        // 绘制圆圈边框
        this.画布上下文.stroke();
      });
    },

    // 重绘所有已保存的矩形
    重绘所有矩形() {
      const 画布 = this.$refs.画布引用;
      // 清空画布（保留透明背景，显示底层图片）
      this.画布上下文.clearRect(0, 0, 画布.width, 画布.height);

      // 遍历绘制所有矩形
      this.矩形数组.forEach(矩形 => {
        this.画布上下文.beginPath();
        this.画布上下文.rect(矩形.横坐标, 矩形.纵坐标, 矩形.宽度, 矩形.高度);
        // 悬停/调整/移动中的矩形高亮显示（红色）
        this.画布上下文.strokeStyle = (矩形.唯一标识 === this.悬停的矩形标识 || 矩形.唯一标识 === this.调整中的矩形标识 || 矩形.唯一标识 === this.移动中的矩形标识) ? "#ff0000" : "#ff0000";
        this.画布上下文.stroke();
        
        // 为悬停/调整/移动中的矩形绘制控制点
        if (矩形.唯一标识 === this.悬停的矩形标识 || 矩形.唯一标识 === this.调整中的矩形标识 || 矩形.唯一标识 === this.移动中的矩形标识) {
          this.绘制矩形控制点(矩形);
        }
      });
    },

    // 清空画布（保留方法，可按需调用）
    清空画布() {
      const 画布 = this.$refs.画布引用;
      this.画布上下文.clearRect(0, 0, 画布.width, 画布.height);
      this.矩形数组 = [];
      this.下一个矩形标识 = 1;
      // 清空画布后派发事件
      this.派发矩形数据变化事件('clear');
    },
  },
};
</script>

<style scoped>
.canvas-container {
  height: 100%;
  overflow: hidden;
  /* border-radius: 34px; */
  position: relative;
  z-index: 10;
}

/* 背景图片容器样式 */
.canvas-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: 100% 100%; /* 保持图片比例，完整显示 */
  background-repeat: no-repeat; /* 不重复 */
  z-index: -1; /* 放在Canvas下方 */
  pointer-events: none;          /* 禁止所有鼠标事件 */
  -webkit-user-drag: none;       /* 禁用webkit内核浏览器的图片拖拽 */
}

.canvasRef {
  width: 100%;
  height: 100%;
  cursor: crosshair;
  display: block;
  /* 禁止文字选中（右键时） */
  user-select: none;
  /* 设置Canvas背景透明，显示底层图片 */
  background-color: transparent;
}
</style>